import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import { useFamilyDocs, useProjectDocs } from '../../db/useScopedDocs';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';

/**
 * Every Befund and every Planum/Profil description of the project's WHOLE
 * Teilmaßnahme family.
 *
 * Family-wide rather than project-wide on purpose: Befunde are shared across a
 * family, so a Foto, Fund, Zeichenblatt or Gesamtbeschreibung may reference a
 * description recorded in a sibling phase. Loading only this project's would
 * leave those references unlabelled.
 */
export function useFamilyDescriptions(projectId: string) {
    const familyIds = useFamilyProjectIds(projectId);
    const { results: findings, loading: loading } =
        useFamilyDocs<FindingDocType>('findings', familyIds);
    const { results: planums } = useFamilyDocs<FindingPlanumDocType>(
        'finding_planums',
        familyIds
    );
    const { results: profiles } = useFamilyDocs<FindingProfileDocType>(
        'finding_profiles',
        familyIds
    );
    return { findings, planums, profiles, loading };
}

/**
 * The same three collections, but only what THIS project recorded.
 *
 * The project scope is right where the rows are the subject rather than the
 * label of a reference: the Befund tree, the field Befund list, a project's
 * parcel usage.
 */
export function useProjectDescriptions(projectId: string) {
    const { results: findings, loading: loading } =
        useProjectDocs<FindingDocType>('findings', projectId);
    const { results: planums } = useProjectDocs<FindingPlanumDocType>(
        'finding_planums',
        projectId
    );
    const { results: profiles } = useProjectDocs<FindingProfileDocType>(
        'finding_profiles',
        projectId
    );
    return { findings, planums, profiles, loading };
}

/**
 * Every Befund and every Planum/Profil description of one scope, plus whether
 * they are still loading. Both hooks above return exactly this.
 */
export type Descriptions = ReturnType<typeof useFamilyDescriptions>;
